import { Button, Select, SelectV2, Spinner } from '@cimplico/components';
import { useEffect, useState } from 'react';
import { toastNotification } from '../../../../../components/toast/Toast';
import { useCreateFYIEntityMapping } from '../../../../../queries/use-create-fyi-entity-map';
import { useFYIEntities } from '../../../../../queries/use-fyi-entities';
import { Entity } from '../../../../../types/entity';
import { FYIEntity } from '../../../../../types/fyi-docs';
import { useIsActiveSubscription } from '../../../../../hooks/use-is-active-subscription';
import { useFirmContext } from '../../../../../providers/FirmProvider';

type FYIDocsEntityConfigurationProps = {
    entity: Entity;
};

function FYIDocsEntityConfiguration({ entity }: FYIDocsEntityConfigurationProps): JSX.Element {
    const fyiEntityQuery = useFYIEntities();
    const createFyiMappingQuery = useCreateFYIEntityMapping(entity.id);
    const activeSubscription = useIsActiveSubscription();

    const { integrations } = useFirmContext();

    const [selectedEntity, setSelectedEntity] = useState<{ value?: FYIEntity; error?: string }>({});
    const [search, setSearch] = useState<string>();

    useEffect(() => {
        const entities = fyiEntityQuery.data || [];

        if (entity.integrations.fyi) {
            // Find a matching entity
            const matching = entities.find((e) => e.id === entity.integrations.fyi?.id);
            setSelectedEntity({ value: matching });
        } else {
            // Find a matching entity
            const matching = entities.find((e) => e.name.toLowerCase() === entity.name.toLowerCase());
            setSelectedEntity({ value: matching });
        }
    }, [fyiEntityQuery.data, entity]);

    function handleEntityChange(id?: number): void {
        const fyiEntity = fyiEntityQuery.data?.find((e) => e.id === id);

        setSelectedEntity({ value: fyiEntity });
    }

    function handleSubmit(e: React.FormEvent<HTMLFormElement>): void {
        e.preventDefault();
        if (!selectedEntity.value) {
            setSelectedEntity({ error: 'Please select an entity' });
            return;
        }
        createFyiMappingQuery.mutate(
            {
                entityId: entity.id,
                fyiEntityId: selectedEntity.value.id,
                fyiEntityName: selectedEntity.value.name,
            },
            {
                onSuccess: () => {
                    toastNotification('success', 'Successfully mapped entity!');
                },
                onError: () => {
                    toastNotification('error', 'Unable to map entity.');
                },
            }
        );
    }

    if (fyiEntityQuery.isLoading) {
        return (
            <div className="flex items-center gap-2">
                <p className="text-sm text-gray-600">Getting clients from FYI...</p>
                <Spinner size="sm" />
            </div>
        );
    }
    if (fyiEntityQuery.isError || !fyiEntityQuery.isSuccess) {
        return <p>Error!</p>;
    }

    const entities = fyiEntityQuery.data;

    return (
        <div className="w-[400px]">
            {!entity.integrations.fyi && (
                <form onSubmit={handleSubmit}>
                    <p className="text-sm text-gray-800 font-medium">Available Clients</p>
                    <label className="text-sm text-gray-600 mb-2 block">
                        This will automatically sync all documents not assigned to a job in the{' '}
                        <strong>{integrations.fyi?.cabinet?.name}</strong> cabinet.
                    </label>
                    <SelectV2
                        value={selectedEntity.value ? [{ label: selectedEntity.value.name, value: selectedEntity.value.id }] : []}
                        options={entities
                            .filter((e) => e.name.toLowerCase().includes(search?.toLowerCase() ?? ''))
                            .map((e) => ({
                                label: e.name,
                                value: e.id,
                                isSelected: selectedEntity.value?.id === e.id,
                                isDisabled: Boolean(e.existingMapping),
                                disabledHint: 'This fyi entity is already mapped.',
                            }))}
                        onChange={(v): void => handleEntityChange(+v[0]?.value)}
                        errorMessage={selectedEntity?.error}
                        disabled={!activeSubscription}
                        onSearch={setSearch}
                    />
                    <div className="mt-2 float-right">
                        <Button
                            label={createFyiMappingQuery.isLoading ? 'Saving...' : 'Save'}
                            isLoading={createFyiMappingQuery.isLoading}
                            size="sm"
                            type="primary"
                            isSubmit
                            disabled={!activeSubscription || createFyiMappingQuery.isLoading}
                        />
                    </div>
                </form>
            )}
        </div>
    );
}
export default FYIDocsEntityConfiguration;
